import React, { useState, useEffect } from 'react';
import { invitations, plans } from '../api';
import { Copy, Trash2, ExternalLink, Check } from 'lucide-react';

const ManageInvitationsPage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [userInvitations, setUserInvitations] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [email, setEmail] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const [maxUses, setMaxUses] = useState(1);
  const [bypassEmailVerification, setBypassEmailVerification] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load all plans, including hidden ones as we're in admin mode
      const [plansRes, invitationsRes] = await Promise.all([
        plans.getPlans(),
        invitations.list()
      ]);
      
      setAvailablePlans(plansRes.data);
      setUserInvitations(invitationsRes.data);
      
      if (plansRes.data.length > 0) {
        setSelectedPlan(plansRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading invitation data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      const invitationData = {
        plan_id: parseInt(selectedPlan),
        email: email || undefined, // Only send if provided
        expires_in_days: parseInt(expiryDays),
        max_uses: parseInt(maxUses),
        bypass_email_verification: bypassEmailVerification
      };

      await invitations.create(invitationData);
      
      // Reset form
      setEmail('');
      setExpiryDays(7);
      setMaxUses(1);
      setBypassEmailVerification(false);
      setSuccess('Invitation created successfully!');
      
      // Reload invitations
      loadData();
    } catch (error) {
      console.error('Error creating invitation:', error);
      setError(error.response?.data?.detail || 'Failed to create invitation. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeInvitation = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this invitation? This action cannot be undone.')) {
      return;
    }

    try {
      await invitations.revoke(id);
      setSuccess('Invitation revoked successfully!');
      
      // Update the invitations list
      setUserInvitations(prev => 
        prev.map(inv => inv.id === id ? { ...inv, is_revoked: true } : inv)
      );
    } catch (error) {
      console.error('Error revoking invitation:', error);
      setError('Failed to revoke invitation. Please try again.');
    }
  };

  const copyToClipboard = (text, id) => {
    // Extract just the token value without any path components
    let token;
    
    if (text.includes('?token=')) {
      // If it's in query parameter format, extract just the token
      token = text.split('?token=')[1];
    } else if (text.includes('/invitation/')) {
      // If it's in path format, extract just the token
      token = text.replace(/.*\/invitation\//, '');
    } else if (text.includes('/accept-invitation/')) {
      // If it's in another path format, extract just the token
      token = text.replace(/.*\/accept-invitation\//, '');
    } else {
      // It might already be just the token
      token = text;
    }
    
    // Construct a clean hash-based URL with just the token in query parameter
    const fullUrl = `${window.location.origin}/#/accept-invitation?token=${token}`;
    
    navigator.clipboard.writeText(fullUrl)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <h1 className="text-2xl font-bold mb-6">Manage Plan Invitations</h1>
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Manage Plan Invitations</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {success}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Create New Invitation</h2>
        
        <form onSubmit={handleCreateInvitation}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan
              </label>
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                {availablePlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} (${plan.price})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (Optional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Leave blank for any email"
              />
              <p className="mt-1 text-sm text-gray-500">
                If specified, only this email can use the invitation
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expires After (Days)
              </label>
              <input
                type="number"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value)}
                min="1"
                max="365"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Uses
              </label>
              <input
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                How many times this invitation can be used
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bypass Email Verification
              </label>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  checked={bypassEmailVerification}
                  onChange={(e) => setBypassEmailVerification(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Bypass email verification</span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                If checked, users can immediately log in without verifying their email
              </p>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Invitation'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Your Invitations</h2>
        
        {userInvitations.length === 0 ? (
          <p className="text-gray-500 italic">You haven't created any invitations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uses</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bypass Verification</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userInvitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {availablePlans.find(plan => plan.id === invitation.plan_id)?.name || `Plan #${invitation.plan_id}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.email || 'Any'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invitation.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.current_uses} / {invitation.max_uses}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invitation.is_revoked ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Revoked
                        </span>
                      ) : new Date(invitation.expires_at) < new Date() ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Expired
                        </span>
                      ) : invitation.current_uses >= invitation.max_uses ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Used
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.bypass_email_verification ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => copyToClipboard(invitation.invitation_link, invitation.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Copy invitation link"
                        >
                          {copiedId === invitation.id ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                        
                        <a
                          href={invitation.invitation_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900"
                          title="Open invitation link"
                        >
                          <ExternalLink size={16} />
                        </a>
                        
                        {!invitation.is_revoked && (
                          <button
                            onClick={() => handleRevokeInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Revoke invitation"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageInvitationsPage;
